import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Button,
  Chip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { formatPrice, calculateDiscount } from '../../utils/formatPrice';

const   ProductCard = ({ product, showAddToCart = true }) => {
  const navigate = useNavigate();
  const { addToCart, addToWishlist, removeFromWishlist, isInWishlist, isInCart } = useCart();
  const { isAuthenticated, openLoginModal } = useAuth();

  // Use weight-based pricing if available, otherwise use default price
  const displayPrice = product.ProductPricing && product.ProductPricing['250g']
    ? product.ProductPricing['250g'].price
    : product.ProductPrice;
  const displayMRP = product.ProductPricing && product.ProductPricing['250g']
    ? product.ProductPricing['250g'].mrp
    : product.ProductMRP;

  const discount = calculateDiscount(displayMRP, displayPrice);
  const inWishlist = isInWishlist(product._id);
  const inCart = isInCart(product._id);

  const handleAddToCart = (e) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      openLoginModal();
      return;
    }
    addToCart(product);
  };

  const handleWishlistToggle = (e) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      openLoginModal();
      return;
    }
    if (inWishlist) {
      removeFromWishlist(product._id);
    } else {
      addToWishlist(product);
    }
  };

  const handleCardClick = () => {
    navigate(`/viewproduct/${product._id}`);
  };

  return (
    <Card
      onClick={handleCardClick}
      sx={{
        width: { xs: '100%', sm: 250 },
        height: 380,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        borderRadius: 2,
        overflow: 'hidden',
        '&:hover': {
          transform: 'translateY(-8px)',
          boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
        },
      }}
    >
      {/* Discount Badge */}
      {discount > 0 && (
        <Chip
          label={`${discount}% OFF`}
          size="small"
          sx={{
            position: 'absolute',
            top: 10,
            left: 10,
            zIndex: 1,
            bgcolor: '#e53935',
            color: '#fff',
            fontWeight: 'bold',
            fontSize: '0.7rem',
          }}
        />
      )}

      {/* Wishlist Button */}
      <IconButton
        onClick={handleWishlistToggle}
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 1,
          bgcolor: 'rgba(255,255,255,0.9)',
          '&:hover': { bgcolor: '#fff' },
        }}
      >
        {inWishlist ? (
          <FavoriteIcon sx={{ color: '#e53935' }} />
        ) : (
          <FavoriteBorderIcon sx={{ color: '#666' }} />
        )}
      </IconButton>

      {/* Product Image */}
      <Box
        sx={{
          height: 160,
          minHeight: 160,
          maxHeight: 160,
          bgcolor: '#f5f5f5',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          p: 1,
        }}
      >
        <img
          src={product.ProductMainImgUrl}
          alt={product.ProductName}
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
          }}
        />
      </Box>

      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 2 }}>
        {/* Product Name */}
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            height: 48,
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            mb: 1,
            color: '#333',
          }}
        >
          {product.ProductName}
        </Typography>

        {/* Short Description */}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            height: 40,
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            mb: 1,
          }}
        >
          {product.ProductShortDesc}
        </Typography>

        {/* Price Section */}
        <Box sx={{ mt: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
              Rs.{formatPrice(displayPrice)}
            </Typography>
            {displayMRP > displayPrice && (
              <Typography
                variant="body2"
                sx={{
                  textDecoration: 'line-through',
                  color: '#999',
                }}
              >
                Rs.{formatPrice(displayMRP)}
              </Typography>
            )}
          </Box>

          {/* Add to Cart Button */}
          {showAddToCart && (
            <Button
              variant={inCart ? 'outlined' : 'contained'}
              fullWidth
              startIcon={<ShoppingCartIcon />}
              onClick={handleAddToCart}
              sx={{
                bgcolor: inCart ? 'transparent' : '#8B4513',
                color: inCart ? '#8B4513' : '#fff',
                borderColor: '#8B4513',
                '&:hover': {
                  bgcolor: inCart ? 'rgba(139,69,19,0.1)' : '#6d3610',
                },
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              {inCart ? 'In Cart' : 'Add to Cart'}
            </Button>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default ProductCard;
